// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IDeceptionControlCache } from 'src/types/d3ePolicy';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MatRipple } from '@angular/material/core';
import {
  FILENAMES_REGEX,
  FOLDER_NAME_REGEXP,
  NO_EXE_OR_DLL_REGEXP, OTHER_VARS_REGEXP,
  ROOT_REGEX,
  VARIABLE_REGEX
} from 'src/types/common';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-data-deception-files',
    templateUrl: './data-deception-files.component.html',
    styleUrls: ['./data-deception-files.component.scss'],
    standalone: false
})
export class DataDeceptionFilesComponent implements OnChanges, AfterViewInit {
  @Input()show_environment_variables: boolean;
  @Input()deceptionFiles: string[] = [];
  @Output()deceptionFilesChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output()deceptionFileErrorStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(MatRipple) ripple: MatRipple;
  @ViewChildren('folderPathInputs', { read: ElementRef}) folderPathInputs: QueryList<ElementRef>;
  
  folderFormGroup: UntypedFormGroup;
  folders: UntypedFormArray;
  toggleGroupAppearance: 'legacy' | 'standard' = 'standard'; // make input?
  policyEnabled = true; // make input?
  selectedIndex: number | null = null;
  cachedControlValues: IDeceptionControlCache[] = [];
  deceptionFileErrors: string[] = [];

  recommendedFolderPath = '${USERPROFILE}\\Documents\\passwords.xls';

  constructor(private formBuilder: UntypedFormBuilder, private translate: TranslateService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deceptionFiles && changes.deceptionFiles.currentValue) {
      this.cachedControlValues = [];

      if (this.folderFormGroup) {
        const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
        const length = folders.length;
        for (let i = 0; i < length; i++) {
          folders.removeAt(0);
        }
        this.deceptionFiles = [];
        this.deceptionFileErrors = [];
      }

      this.updatePolicyFolders(changes.deceptionFiles.currentValue);
    }
    
  }

  ngAfterViewInit(): void {
    if (this.ripple) {
      this.ripple.disabled = true;
    }
  }

  onInputChange(event, control, index) {
    const folderPath = control.value.path.trim();
    this.cachedControlValues[index].newValue = folderPath;
    this.validateDirectories(folderPath, 'file', index);

    this.cachedControlValues[index].originalValue = control.value.path;
    this.cachedControlValues[index].newValue = control.value.path;

    this.deceptionFiles.splice(index, 1, control.value.path);

    control.patchValue({
      path: control.value.path
    });

    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'file', i);
      }
    });
    
    this.deceptionFilesChanged.emit(this.deceptionFiles);

  }

  onFocusIn(event, index) {
    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'file', i);
      }
    });
  }

  onFocusOut(event, index) {
    this.validateDirectories(this.cachedControlValues[index].newValue, 'file', index);
  }

  hasFolderError(i) {
    let hasError = false;
    if (this.deceptionFileErrors[i] !== null) {
      hasError = true;
    }
    return hasError;
  }

  updatePolicyFolders(deceptionFiles: string[]): void {
    this.deceptionFiles = deceptionFiles;
    if (this.deceptionFiles.length === 0) {
      this.folderFormGroup = this.formBuilder.group({
        folders: this.formBuilder.array([this.createFileFormatGroup('', false)])
      });

        this.cachedControlValues = [];
        this.cachedControlValues.push({originalValue: '', newValue: ''});
    } else {
      this.deceptionFiles.sort((a, b) => {
        if (a.toLocaleLowerCase() === this.recommendedFolderPath.toLocaleLowerCase()) {
          return -1;
        }
        if (b.toLocaleLowerCase() === this.recommendedFolderPath.toLocaleLowerCase()) {
          return 1;
        }
        if (a.toLocaleLowerCase() === this.recommendedFolderPath.toLocaleLowerCase()) {
          return 0;
        }
        return a.localeCompare(b);
      });

      this.deceptionFiles.forEach((value, index) => {
        if (this.folderFormGroup) {
          this.addFolderFormGroup(value);
        } else {
          this.folderFormGroup = this.formBuilder.group({
            folders: this.formBuilder.array([this.createFileFormatGroup(value)])
          });
        }
        this.cachedControlValues.push({originalValue: value, newValue: value});

      });

    }
  }

  get controls()  {
    return (this.folderFormGroup.get('folders') as UntypedFormArray).controls;
  }

  onAddVariable(control, index: number, variable: string) {
    const path = control.value.path;
    const newPath = variable.concat(path);

    this.selectedIndex = index;
    this.cachedControlValues[index].newValue = newPath;

    this.validateDirectories(this.cachedControlValues[index].newValue, 'file', index);

    if (this.deceptionFileErrors[index] === null) {
      this.deceptionFiles[index] = newPath;
      // this.deceptionFiles.forEach(f =>  {
      //   this.deceptionFiles[index] = f.toLowerCase() === newPath.toLowerCase() ? newPath : f;
      // });
    }

    control.patchValue({
        path: newPath
    });

    this.deceptionFilesChanged.emit(this.deceptionFiles);

  }

  removeDirectoryFromPolicy(index: number) {
    this.deceptionFiles.splice(index, 1);
  }

  public addFolderFormGroup(path: string = '') {
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    // const folderSetting = setting.trim() !== '' ? setting : `medium-${folders.length}`;
    
    if (folders.value.some(v => v.path === '')) {
      return;
    }

    folders.push(this.createFileFormatGroup(path));

  }

  public onAddFolderClick() {
    const path = '';
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    
    if (folders.value.some(v => v.path === '')) {
      // TODO: Warn user they have a new folder that needs a path before creating a new folder.
      return;
    }

    folders.push(this.createFileFormatGroup(path, false));
    this.cachedControlValues.push({originalValue: path, newValue: path});
    
    // Make this dirty so we see enable the cancel/save buttons
    this.deceptionFilesChanged.emit(this.deceptionFiles);
  }

  removePathRow(path, index: number) {
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    
    if (folders.length > 0 && index < folders.length) {
      folders.removeAt(index);

      this.removeDirectoryFromPolicy(index);
      this.cachedControlValues.splice(index, 1);

      if (folders.length === 0) {
        this.onAddFolderClick();
      }

      this.deceptionFileErrors.splice(index, 1);

      this.controls.forEach((c, i) => {
        const p = c.get('path').value || '';
        this.validateDirectories(p, 'file', i);
      });

      this.deceptionFilesChanged.emit(this.deceptionFiles);
    }
  }

  private createFileFormatGroup(path: string, validate = true) {
    const length = this.deceptionFileErrors.push(null);
    if (validate) {
      this.validateDirectories(path, 'file', length - 1);
    }

    return new UntypedFormGroup({
      'path': new UntypedFormControl(path.trim(), Validators.nullValidator)
    });
  }

  validateDirectories(value: String, type: 'directory' | 'file', index: number): void {
    let error = null;
    const folderPath = value.trim();

    if (!(this.cachedControlValues.length === 1 && folderPath === '')) {
      // test the basic syntax of the line
      if (!FOLDER_NAME_REGEXP.test(folderPath) ) {
        error = this.translate.instant('ERROR_INVALID_DIR_START', {
          type: type
        });

      }

      let driveAndPath;
      if (folderPath.includes(':')) {
        driveAndPath = folderPath.split(':');
      } else {
        // no : means it starts with a variable, fake out the drive letter here to allow for processing.
        // The drive letter is not looked at further down anyhow.
        driveAndPath = [ '', folderPath ];
      }

      if (this.cachedControlValues.length > index) {
        this.cachedControlValues.forEach((c, i) => {
          if (c.newValue.toLocaleLowerCase() === folderPath.toLocaleLowerCase() && i !== index) {
            error = this.translate.instant('ERROR_DIR_EXISTS');
          }
        });
      }

      if (driveAndPath.length > 1) {
        const path_without_drive = driveAndPath[1];
        let test_variables = path_without_drive.trim();
        // make sure no special root files are specified
        if (ROOT_REGEX.test(path_without_drive.trim()) ) {
          error = 'Invalid system file: ' + folderPath;
        } else if (VARIABLE_REGEX.test(test_variables)) {
            // allow only some variables, any other variables are NOT allowed.
            test_variables = test_variables.replace(VARIABLE_REGEX, 'replaced');
        }

        if (!OTHER_VARS_REGEXP.test(test_variables)) {
          error = this.translate.instant('ERROR_VARIABLE_PATH', {type: type});
        } else {
          const filenames = path_without_drive.split('\\');
          for (const filename of filenames) {
            if (FILENAMES_REGEX.test(filename.trim())) {
              error = this.translate.instant('ERROR_PATH_NO_DRIVE', {type, filename});
            }
            if (NO_EXE_OR_DLL_REGEXP.test(filename.trim())) {
              error = this.translate.instant('ERROR_PROHIBITED_EXTS', {type, filename});
            }
          }
        }

      }

      if (folderPath.length < 1) {
        error = this.translate.instant('ERROR_EMPTY_PATHS');
      }

    }

    this.deceptionFileErrors[index] = error;
    const hasError = !this.deceptionFileErrors.every(c => c === null);
    this.deceptionFileErrorStateChanged.emit(hasError);

  }

  onSetRecommendedClick() {
    const recommendedExists = this.deceptionFiles.some(f =>
      f.toLocaleLowerCase() === this.recommendedFolderPath.toLocaleLowerCase()
    );
    if (recommendedExists) {

      this.cachedControlValues = [];

      if (this.folderFormGroup) {
        const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
        const length = folders.length;
        for (let i = 0; i < length; i++) {
          folders.removeAt(0);
        }
        this.deceptionFileErrors = [];
      }

      this.updatePolicyFolders(this.deceptionFiles);

      setTimeout(() => {
        this.ripple.launch({
          centered: true,
          animation: {
            enterDuration: 500,
            exitDuration: 1000
          }
        });
        setTimeout(() => {
          this.ripple.disabled = true;
        }, 1500);
      }, 0);

    } else {
      this.ripple.disabled = true;
      this.onAddFolderClick();
      const controlsLength = this.controls.length - 1;
      let newControl = this.controls[this.controls.length - 1];
      this.onAddVariable(newControl, controlsLength, this.recommendedFolderPath);

    }
  }

}
